<template>
    <article class="page">
        <article-header :section-type-class="'section'" bg-class="" float-image>
            <template v-slot:firstCol>
                <h1>BarnehageHMS - et verktøy for bedre jobbhelse</h1>
                <p class="no-pre">
                  BarnehageHMS gjør det enkelt å komme i gang med gode samtaler om arbeidsmiljøet i barnehagen.
                </p>
                <p class="no-pre">
                  Her finner dere 15 HMS-temaer med korte tekster om de mest relevante faktorene som påvirker arbeidsmiljøet i barnehage.
                  Hvert tema inneholder også konkrete tips, råd og diskusjonspunkter som kan brukes direkte i personalgruppa.
                </p>
                <p class="no-pre">
                  Verktøyet kan brukes på ulike måter – som støtte i det systematiske HMS-arbeidet,
                  til oppfølging av tema som er aktuelle for dere og som utgangspunkt for felles refleksjon og utvikling i personalet.
                  Bruk BarnehageHMS på den måten som passer best for dere – enten som en fast del av arbeidet, eller mer fleksibelt ved behov.
                </p>
                <p class="no-pre">Verktøyet er utviklet av IA-bransjeprogram barnehage.
                  Bransjeprogrammet jobber kunnskapsbasert og partssammensatt med ulike tiltak for å forebygge og redusere sykefraværet i barnehager,
                  i offentlig og privat sektor. Tiltakene i IA-bransjeprogram barnehage er laget i samarbeid med og for barnehager –
                  <a href="https://www.ks.no/fagomrader/arbeidsgiverpolitikk/ia-bransjeprogram/bransjeprogram-barnehage/">
                    les mer her
                  </a>
                </p>
            </template>
            <template v-slot:secondCol>
                <img
                    src="../assets/images/illustration_banner_start.svg"
                    alt=""
                    class="w-100"
                />
            </template>
        </article-header>

        <div class="section--lg flex-grow">
            <div class="container bg--pink--light pb-40 pt-40 px-70">
                <div class="row">
                    <div class="col-md-6">
                        <div class="article-header__first-col">
                            <h2 class="mb-20">
                                Arbeidsmiljø handler om arbeidet
                            </h2>
                            <p class="no-pre">
                              Arbeidsmiljø handler om arbeidet, og er knyttet til hvordan vi i felleskap planlegger, organiserer og gjennomfører arbeidet.
                              Arbeidsmiljøet påvirker både helse, engasjement, nærvær og kvaliteten i barnehagene. Noen arbeidsmiljøfaktorer går igjen i alle barnehager, men mye er også forskjellig.
                              Gjennom BarnehageHMS får alle i barnehagen tilgang til kunnskap om faktorer som påvirker arbeidsmiljø, jobbhelse, engasjement og kvalitet i barnehagen.
                            </p>
                            <p class="no-pre">
                              Partene på arbeidsplassen, styrer, tillitsvalgte og verneombud, har et hovedansvar i arbeidsmiljøarbeidet,
                              men det er viktig at hele personalet er involvert.
                            </p>
                        </div>
                    </div>
                    <div class="col-md-6 d-flex flex-column align-items-center">
                        <img
                            src="../assets/images/illustration_block_start.svg"
                            alt=""
                            class="w-100"
                        />
                        <router-link
                            v-if="getStartedTema"
                            :to="{
                                name: routeNames.tema,
                                params: {
                                    id: getStartedTema.id,
                                },
                            }"
                            class="pink ms-5"
                        >
                            Les mer om hvordan komme i gang
                        </router-link>
                    </div>
                </div>
            </div>
        </div>

        <div id="temaer" class="section flex-grow bg--light">
            <div class="container">
                <h1 class="mb-20">HMS-tema</h1>
                <p class="no-pre">
                   Vi anbefaler at leder, tillitsvalgt og verneombud starter med tema «Kom i gang med arbeidsmiljøarbeidet» –
                   her finner dere også lenker til andre nyttige ressurser i arbeidet.
                   I valg av tema videre anbefaler vi at dere jobber med det som er viktig for dere på akkurat deres arbeidsplass.
                </p>
                <p class="no-pre pb-sm-4">
                    Det viktigste dere gjør nå, er å sette av tid til arbeidet.
                    Lykke til!
                </p>
                <ul v-if="temaer.length" class="row list--unstyled">
                    <li class="col-md-4" v-for="tema in temaer" :key="tema.id">
                        <TemaListItem
                            :id="tema.id"
                            :tittel="tema.tittel"
                            :bilde="tema.bilde"
                        />
                    </li>
                </ul>
            </div>
        </div>

        <section class="section section--lg flex-grow responsive-layout">
            <div class="container">
                <div class="row">
                    <div class="col-md-6 d-flex justify-content-center">
                        <img src="../assets/images/ia_logo_ny.png" alt="IA - Bransjeprogram Barnehage" />
                    </div>
                    <div class="col-md-6 d-flex align-items-center">
                        <div>
                            <h3 class="mb-5">
                                IA-bransjeprogram barnehage står bak dette
                                verktøyet som kan brukes av alle barnehager:
                            </h3>
                            <p class="no-pre">
                                LO, Unio, YS, KS, Virke, NHO, Spekter,
                                Fagforbundet, Utdanningsforbundet, Delta,
                                Norlandia, FUS, PBL, KA, NAV og Arbeidstilsynet
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </article>
</template>

<script>
    import ArticleHeader from '../components/Framework/ArticleHeader';
    import { useAuth } from '@/composables/useAuth';
    import TemaListItem from '@/components/List/TemaListItem.vue';
    import { onMounted, ref } from 'vue';
    import routeNames from '@/router/routeNames';
    import api from '@/scripts/api';

    export default {
        setup() {
            const { loggedInStatus, isLoggedIn, isOrgAdmin, isSysAdmin } =
                useAuth();
            const temaer = ref([]);
            const getStartedTema = ref(null);

            const fetchTemaer = async () => {
                try {
                    const response = await api.getTemaer();

                    //Id of the 'How to get started' theme. This will be different from production/dev/test etc so we require multiple ids
                    const getStartedIds = new Set([
                        'fb978a6b-9ca2-4edf-bc0f-9a76751c3636',
                        'id2',
                    ]);

                    // Clear existing data
                    temaer.value = [];
                    getStartedTema.value = null;

                    response.forEach((subject) => {
                        if (getStartedIds.has(subject.id)) {
                            getStartedTema.value = subject;
                        } else {
                            temaer.value.push(subject);
                        }
                    });
                } catch (error) {
                    console.error('Failed to fetch temaer:', error);
                }
            };
            onMounted(fetchTemaer);

            return {
                loggedInStatus,
                isLoggedIn,
                isOrgAdmin,
                isSysAdmin,
                routeNames,
                getStartedTema,
                temaer,
            };
        },
        components: {
            TemaListItem,
            'article-header': ArticleHeader,
        },
    };
</script>
